"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { InputIcon, Input, Label, Button } from "keep-react";
import { Lock, Eye, EyeSlash, CheckCircle } from "phosphor-react";
import { ShowPasswordStrength } from "@components/auth/ShowPass";
import { passwordStrengthChecker } from "@/helpers/checkPasswordStrength";
import { PassCheckList } from "@components/auth/PassCheckList";
import { pasteFilter, keyDownFilter } from "@/helpers/checkPasswordFilters";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { closeChangePassword } from "@store/features/userActionsSlice";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/ui/dialog";
import { OTPInput } from "@components/auth/OTPInput";
import { toast } from "sonner";
import { useRequestPasswordResetMutation, useResetPasswordMutation } from "@store/api-helpers/userApi";
import { 
  setCurrentStep,
  resetState,
  sendEmailSuccess
} from "@store/features/passwordRecoverySlice";
import { PasswordChangeStep } from "@models/password-recovery.model";
import { useStyleSchema } from "@components/styleSchema/StyleSchemaHandler";
import { StandartAppButton } from "@components/StandartAppButton";

/**
 * Password schema for validation
 * Requires:
 * - Minimum 6 characters
 * - At least 1 uppercase letter
 * - At least 1 number
 * - At least 1 special character
 */
const passwordSchema = z.object({
  newPassword: z
    .string()
    .min(6, { message: "Minimum 6 characters" })
    .refine(
      (password) => /[A-Z]/.test(password),
      { message: "At least 1 uppercase letter" }
    )
    .refine(
      (password) => /\d/.test(password),
      { message: "At least 1 number" }
    )
    .refine(
      (password) => /[^A-Za-z0-9]/.test(password),
      { message: "At least 1 special character" }
    ),
  confirmPassword: z.string().min(1, { message: "Please confirm your password" }),
}).refine(
  (data) => data.newPassword === data.confirmPassword, 
  {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  }
);

type PasswordFormData = z.infer<typeof passwordSchema>;
type Strength = 0 | 1 | 2 | 3;
type Length = number;
type Contains = string[];

/**
 * ChangePasswordModal Component
 * 
 * A multi-step modal for changing user passwords with the following steps:
 * 1. Email confirmation and reset code request
 * 2. OTP (One-Time Password) verification
 * 3. New password input and confirmation
 * 4. Success confirmation
 * 
 * @returns React component for password change flow
 */
export const ChangePasswordModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isOpen = useSelector((state: RootState) => state.userActions.isChangePasswordOpen);
  const profile = useSelector((state: RootState) => state.user.profile);
  const { verificationCode, email, currentStep: persistedStep } = useSelector((state: RootState) => state.passwordRecovery);
  const { currentSchema } = useStyleSchema();
  const buttonColor = currentSchema.colors?.primary?.hex;

  // State for managing the multi-step flow
  const [currentStep, setLocalStep] = useState<PasswordChangeStep>(
    persistedStep !== undefined ? persistedStep as unknown as PasswordChangeStep : PasswordChangeStep.EMAIL_INPUT
  );
  const [userEmail, setUserEmail] = useState<string>("");
  
  // Email-Adresse aktualisieren, wenn sich das Profil ändert
  useEffect(() => {
    if (profile?.email) {
      setUserEmail(profile.email);
    }
  }, [profile?.email]);
  // RTK Query mutations
  const [requestPasswordReset, { isLoading: isRequestingReset }] = useRequestPasswordResetMutation();
  const [resetPassword, { isLoading: isResettingPassword }] = useResetPasswordMutation();

  // Password field states
  const [passwordState, setPasswordState] = useState({
    visibleNew: false,
    visibleConfirm: false,
    focusedNew: false,
    focusedConfirm: false,
    isMismatch: false,
  });

  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState({
    error: "",
    strength: 0 as number,
    contains: [] as Contains,
    length: 0 as Length,
    isValid: false
  });

  // Password form setup
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    watch: watchPassword,
    trigger: triggerPasswordValidation,
    reset: resetPasswordForm
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    mode: "onChange"
  });

  // Watch password values
  const newPassword = watchPassword("newPassword") || "";
  const confirmPassword = watchPassword("confirmPassword") || "";

  /**
   * Update password validation when password inputs change
   */
  useEffect(() => {
    if (currentStep !== PasswordChangeStep.PASSWORD_INPUT) return;

    // Check password requirements
    const result = passwordStrengthChecker(newPassword);
    
    // Perform Zod validation
    const validationResult = z.string()
      .min(6, { message: "Minimum 6 characters" })
      .refine((password) => /[A-Z]/.test(password), { message: "At least 1 uppercase letter" })
      .refine((password) => /\d/.test(password), { message: "At least 1 number" })
      .refine((password) => /[^A-Za-z0-9]/.test(password), { message: "At least 1 special character" })
      .safeParse(newPassword);
    
    const meetsAllRequirements = validationResult.success;
    const passwordsMatch = newPassword === confirmPassword;
    
    // Update validation state
    setPasswordValidation({
      strength: result.id,
      contains: result.contains,
      length: newPassword.length,
      error: !passwordsMatch && confirmPassword ? "Passwords do not match" : "",
      isValid: meetsAllRequirements && passwordsMatch && confirmPassword.length > 0
    });
    
    // Set mismatch state for UI
    setPasswordState(prev => ({
      ...prev,
      isMismatch: confirmPassword.length > 0 && !passwordsMatch,
    }));
    
    // Trigger validation to update errors
    if (newPassword) triggerPasswordValidation("newPassword");
    if (confirmPassword) triggerPasswordValidation("confirmPassword");
    
  }, [newPassword, confirmPassword, triggerPasswordValidation, currentStep]);

  /**
   * Toggle password visibility
   * @param field - Which password field to toggle ('new' or 'confirm')
   */
  const togglePasswordVisibility = (field: 'new' | 'confirm') => {
    setPasswordState(prev => ({
      ...prev,
      [field === 'new' ? 'visibleNew' : 'visibleConfirm']: 
        !prev[field === 'new' ? 'visibleNew' : 'visibleConfirm'],
    }));
  };

  /**
   * Sync local state with Redux state
   */
  useEffect(() => {
    if (persistedStep !== undefined) {
      setLocalStep(persistedStep as unknown as PasswordChangeStep);
    }
    
    if (email && email !== userEmail) {
      setUserEmail(email);
    }
  }, [persistedStep, email, userEmail, currentStep]);

  /**
   * Handle OTP verification (Step 2)
   */
  useEffect(() => {
    
    if (verificationCode && verificationCode.length === 6 && currentStep === PasswordChangeStep.OTP_INPUT) {
      setLocalStep(PasswordChangeStep.PASSWORD_INPUT);
      dispatch(setCurrentStep(PasswordChangeStep.PASSWORD_INPUT));
    }
  }, [verificationCode, currentStep, dispatch]);
  /**
   * Handle email submission (Step 1)
   * Requests password reset code from API
   * 
   * @param e - Form submission event
   */
  const onEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userEmail) {
      toast.error(t("auth-errors.email-required"));
      return;
    }
    try {
      const response = await requestPasswordReset({
        mail: userEmail
      }).unwrap();

      // The API returns a generic message for security reasons
      if (response.message && (
          response.message === "Reset code sent" || 
          response.message.includes("receive a password reset code") ||
          response.message.includes("If your email exists")
      )) {
        
        // Create a timestamp for OTP expiration
        const timestamp = Date.now();
        
        // Update Redux state with email and timestamp
        dispatch(sendEmailSuccess({ email: userEmail, timestamp }));
        
        // Force the step change directly
        setLocalStep(PasswordChangeStep.OTP_INPUT);
        
        toast.success(t("auth.reset-code-sent"));
      }
    } catch {
      toast.error(t("auth-errors.reset-code-error"));
    }
  };

  /**
   * Handle password submission (Step 3)
   * Resets password with verification code and new password
   * 
   * @param data - Form data with new password
   */
  const onPasswordSubmit = async (data: PasswordFormData) => {
    try {
      const response = await resetPassword({
        mail: userEmail,
        resetCode: verificationCode,
        newPassword: data.newPassword
      }).unwrap();

      if (response.message === "Password successfully reset") {
        setLocalStep(PasswordChangeStep.SUCCESS);
        dispatch(setCurrentStep(PasswordChangeStep.SUCCESS));
        toast.success(t("auth.save-password-success"));

        // Reset state and close modal
        setTimeout(() => {
          handleClose();
        }, 10000);
      }
    } catch {
      toast.error(t("auth-errors.password-change-error"));
    }
  };

  /**
   * Close dialog and reset state
   */
  const handleClose = () => {
      dispatch(closeChangePassword());
      dispatch(resetState());
      resetPasswordForm();
      setPasswordState({
        visibleNew: false,
        visibleConfirm: false,
        focusedNew: false,
        focusedConfirm: false,
        isMismatch: false,
      });
      setPasswordValidation({
        error: "",
        strength: 0,
        contains: [],
        length: 0,
        isValid: false
      });
  };

  /**
   * Render content based on current step
   */
  const renderStepContent = () => {
    switch (currentStep) {
      case PasswordChangeStep.EMAIL_INPUT:
        return (
          <form onSubmit={onEmailSubmit}>
            <p className="text-sm">{t("auth.password-change-subtitle.step-1")}</p>
            <div className="space-y-2">
              <div className="text-center py-4 text-xl font-bold">
                {userEmail}
              </div>
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                {t("common.cancel")}
              </Button>
              <button 
                type="submit" 
                className="px-4 py-2 rounded text-center md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: buttonColor,
          color: "white",
        }}
                disabled={isRequestingReset}
              >
                {isRequestingReset ? t("common.loading") : t("auth.request-reset-code")}
              </button>
            </div>
          </form>
        );
      
      case PasswordChangeStep.OTP_INPUT:
        return (
          <div className="space-y-4">
            <p className="text-sm">{t("auth.password-change-subtitle.step-2")}</p>
            <OTPInput />
            <div className="flex justify-end space-x-2 pt-4">
              <button
                type="button"
                className="px-4 py-2 rounded text-center md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: buttonColor,
          color: "white",
        }}
                onClick={handleClose}
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        );
      
      case PasswordChangeStep.PASSWORD_INPUT:
        return (
          <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
            <p className="text-sm">{t("auth.password-change-subtitle.step-3")}</p>
            {/* New Password Field */}
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-sm font-medium">
                {t("auth.new-password")}
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={passwordState.visibleNew ? "text" : "password"}
                  className="ps-11 pe-11"
                  onKeyDown={keyDownFilter}
                  onPaste={pasteFilter}
                  {...registerPassword("newPassword")}
                  onFocus={() => setPasswordState((prev) => ({ ...prev, focusedNew: true }))}
                  onBlur={() => setPasswordState((prev) => ({ ...prev, focusedNew: false }))}
                />
                <InputIcon>
                  <Lock size={19} color="#455468" />
                </InputIcon>
                <InputIcon
                  onClick={() => togglePasswordVisibility('new')}
                  className="absolute pointer-events-auto inset-y-0 start-auto end-3 flex items-center cursor-pointer"
                >
                  {passwordState.visibleNew ? (
                    <EyeSlash size={19} color="#455468" />
                  ) : (
                    <Eye size={19} color="#455468" />
                  )}
                </InputIcon>
              </div>
              {passwordErrors.newPassword && (
                <p className="text-sm text-red-500">
                  {passwordValidation.length >= 6 ? 
                    passwordErrors.newPassword.message : 
                    t("auth-errors.password-min-length")}
                </p>
              )}

              <AnimatePresence>
                <motion.div
                  className="mt-2"
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ShowPasswordStrength strength={passwordValidation.strength as Strength} />
                  <PassCheckList
                    contains={passwordValidation.contains}
                    length={passwordValidation.length}
                  />
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium">
                {t("auth.confirm-password")}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={passwordState.visibleConfirm ? "text" : "password"}
                  className="ps-11 pe-11"
                  onKeyDown={keyDownFilter}
                  onPaste={pasteFilter}
                  {...registerPassword("confirmPassword")}
                />
                <InputIcon>
                  <Lock size={19} color="#455468" />
                </InputIcon>
                <InputIcon
                  onClick={() => togglePasswordVisibility('confirm')}
                  className="absolute pointer-events-auto inset-y-0 start-auto end-3 flex items-center cursor-pointer"
                >
                  {passwordState.visibleConfirm ? (
                    <EyeSlash size={19} color="#455468" />
                  ) : (
                    <Eye size={19} color="#455468" />
                  )}
                </InputIcon>
              </div>
              {passwordErrors.confirmPassword && (
                <p className="text-sm text-red-500">{t("auth-errors.confirm-password-required") || passwordErrors.confirmPassword.message}</p>
              )}
              {passwordValidation.error && !passwordErrors.confirmPassword && (
                <p className="text-sm text-red-500">{passwordValidation.error}</p>
              )}
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <button
                type="button"
                className="px-4 py-2 rounded text-center md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          border: `1px solid ${buttonColor}`,
          color: buttonColor,
        }}
                onClick={handleClose}
              >
                {t("common.cancel")}
              </button>
              {/* <button 
                type="submit" 
                className="px-4 py-2 rounded text-center md:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: buttonColor,
          color: "white",
        }}
                disabled={isResettingPassword || !passwordValidation.isValid}
              >
                {isResettingPassword ? t("common.loading") : t("auth.change-password")}
              </button> */}
              <StandartAppButton
                type="submit"
                buttonColor={buttonColor}
                isDisabled={isResettingPassword || !passwordValidation.isValid}
                isLoading={isResettingPassword}
              >
                {isResettingPassword ? t("common.loading") : t("auth.change-password")}
              </StandartAppButton>
            </div>
          </form>
        );
      
      case PasswordChangeStep.SUCCESS:
        return (
          <div className="flex flex-col items-center justify-center space-y-4 py-6">
            <CheckCircle size={64} weight="fill" className="text-green-500" />
            <h3 className="text-xl font-semibold text-center">
              {t("auth.save-password-success")}
            </h3>
            <p className="text-center text-gray-600">
              {t("auth.redirect-message")}
            </p>
            <Button
              type="button"
              className="bg-primary dark:bg-primary-dark normal-case mt-4"
              onClick={handleClose}
            >
              {t("common.close")}
            </Button>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[425px] bg-white dark:bg-metal-900">
        <DialogHeader>
          <DialogTitle>{t("auth.change-password")}</DialogTitle>
        </DialogHeader>
        {renderStepContent()}
      </DialogContent>
    </Dialog>
  );
};